import { functions } from "@/lib/appwrite"
import { format } from "date-fns"
import type { ServiceReport } from "@/Wist-blockchain/collections/serviceReport/types"
import type { Company } from "@/lib/collections/organizations/getOrganizationsFetch"
import type { Instrument } from "@/Wist-blockchain/collections/instruments/types"
import { getReportFilePreviewUrl } from "./uploadAnexosStorage"
import { parseAdditionalEquipment } from "@/Wist-blockchain/collections/instruments/types"
import { parseAttachment } from "@/Wist-blockchain/collections/serviceReport/types"
import { getInstrumentQrUrl } from "@/Wist-blockchain/collections/instruments/instruments-storage.service"

const FUNCTION_ID = import.meta.env.VITE_DOCS_GENERATOR

// ─── Labels ───────────────────────────────────────────────────────────────────
const SERVICE_TYPE_LABELS: Record<string, string> = {
    instalacion: "Instalación",
    preventivo: "Preventivo",
    correctivo: "Correctivo",
    "calificacion-validacion": "Calificación / Validación",
    capacitacion: "Capacitación",
    otro: "Otro",
}

const SERVICE_REASON_LABELS: Record<string, string> = {
    instalacion: "Instalación",
    "mantenimiento-programado": "Mantenimiento programado",
    "falla-reportada": "Falla reportada",
    "revision-anomalia": "Revisión por anomalía",
    otro: "Otro",
}

const FINAL_STATUS_LABELS: Record<string, string> = {
    operativo: "Operativo",
    operativo_observaciones: "Operativo con observaciones",
    fuera_servicio: "Fuera de servicio",
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDateStr = (dateStr: string | null | undefined): string => {
    if (!dateStr) return "—"
    try {
        const datePart = dateStr.split("T")[0]
        return format(new Date(`${datePart}T12:00:00`), "dd/MM/yyyy")
    } catch { return "—" }
}

const formatTimeStr = (dateTimeStr: string | null | undefined): string => {
    if (!dateTimeStr) return "—"
    return dateTimeStr.split("T")[1]?.slice(0, 5) ?? "—"
}

const calcDuration = (start: string | null | undefined, end: string | null | undefined): string => {
    if (!start || !end) return "—"
    const toMins = (iso: string) => {
        const hhmm = iso.split("T")[1]?.slice(0, 5) ?? ""
        const [h, m] = hhmm.split(":").map(Number)
        return (h ?? 0) * 60 + (m ?? 0)
    }
    const diff = toMins(end) - toMins(start)
    if (diff <= 0) return "—"
    const h = Math.floor(diff / 60)
    const m = diff % 60
    if (h > 0 && m > 0) return `${h}h ${m}min`
    if (h > 0) return `${h}h`
    return `${m}min`
}

const calcAge = (year: number | null | undefined): string => {
    if (!year) return "—"
    const age = new Date().getFullYear() - year
    if (age < 0) return "—"
    if (age === 0) return "Menos de 1 año"
    return age === 1 ? "1 año" : `${age} años`
}

const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
}

// ─── Types ─────────
export interface ActivityDocRow {
    order: number
    activity: string | null
    result: string | null
    observations: string | null
}

export interface MaterialDocRow {
    order: number
    name: string | null
    code: string | null
    quantity: number
    condition: string | null
    unitCost: number
    ivaPercent: number
}

export interface EngineerProfile {
    position: string
    idNumber: string
    phone: string
    email: string
}

export interface PreviousServiceInfo {
    isFirst: boolean
    lastDate: string | null
}

export interface GenerateServiceReportDocOptions {
    report: ServiceReport
    activities: ActivityDocRow[]
    materials: MaterialDocRow[]
    engineerName: string
    engineerProfile?: EngineerProfile
    company?: Company
    instrument?: Instrument
    brandName?: string | null
    previousServiceInfo?: PreviousServiceInfo
}

// ─── Main function ────────────────────────────────────────────────────────────
export const generateServiceReportDoc = async ({
    report,
    activities,
    materials,
    engineerName,
    engineerProfile,
    company,
    instrument,
    brandName,
    previousServiceInfo,
}: GenerateServiceReportDocOptions): Promise<{ success: boolean; error?: string }> => {
    if (!FUNCTION_ID) return { success: false, error: "Falta configuración: VITE_DOCS_GENERATOR" }

    const serviceTypes  = (report.serviceTypes ?? (report.serviceType ? [report.serviceType] : [])) as string[]
    const serviceReason = report.serviceReason ?? ""
    const finalStatus   = report.finalStatus   ?? ""

    const payload = {
        template_type: "service_report",
        data: {
            // ── Sección 1: Datos generales ──────────────────────────────────
            REPORT_CODE:         report.idReport ?? "—",
            SERVICE_DATE:        formatDateStr(report.serviceDate),
            REPORT_DATE:         formatDateStr(report.reportDate),
            SERVICE_START_TIME:  formatTimeStr(report.serviceStartTime),
            SERVICE_END_TIME:    formatTimeStr(report.serviceEndTime),
            DURATION:            calcDuration(report.serviceStartTime, report.serviceEndTime),
            PURCHASE_ORDER:      report.purchaseOrder ?? "—",
            ENGINEER_NAME:       engineerName,

            // Tipo de servicio (checkboxes — soporta multi-selección)
            is_instalacion:  serviceTypes.includes("instalacion"),
            is_preventivo:   serviceTypes.includes("preventivo"),
            is_correctivo:   serviceTypes.includes("correctivo"),
            is_calificacion: serviceTypes.includes("calificacion-validacion"),
            is_capacitacion: serviceTypes.includes("capacitacion"),
            is_otro_tipo:    serviceTypes.includes("otro"),
            SERVICE_TYPE:    serviceTypes.map(t => SERVICE_TYPE_LABELS[t] ?? t).join(", ") || "—",

            // ── Sección 2: Datos del cliente ────────────────────────────────
            CLIENT_NAME:                  company?.name ?? "—",
            CLIENT_COMMERCIAL_NAME:       company?.commercialName ?? "—",
            CLIENT_RUC:                   company?.ruc ? String(company.ruc) : "—",
            CLIENT_ADDRESS:               company?.address ?? "—",
            CLIENT_CITY:                  company?.city ?? "—",
            CLIENT_PHONE:                 report.clientPhone ?? "—",
            CLIENT_EMAIL:                 report.clientEmail ?? "—",
            CLIENT_CONTACT:               report.clientContact ?? "—",
            CLIENT_AREA:                  report.clientArea ?? "—",
            CLIENT_POSITION:              report.clientPosition ?? "—",

            // ── Sección 3: Identificación del equipo ────────────────────────
            EQUIPMENT_NAME:        instrument?.equipmentName ?? "—",
            BRAND_NAME:            brandName ?? "—",
            EQUIPMENT_MODEL:       instrument?.model ?? "—",
            SERIAL_NUMBER:         instrument?.serialNumber ?? "—",
            MANUFACTURE_YEAR:      instrument?.manufactureYear ? String(instrument.manufactureYear) : "—",
            EQUIPMENT_AGE:         calcAge(instrument?.manufactureYear),
            CLIENT_ASSET_CODE:     instrument?.clientAssetCode ?? "—",
            VOLTAGE_CURRENT:       [instrument?.voltage, instrument?.current].filter(Boolean).join(" / ") || "—",
            LOCATION:              instrument?.location ?? "—",
            EQUIPMENT_DESCRIPTION: instrument?.equipmentDescription ?? "—",
            ADDITIONAL_EQUIPMENT:  instrument?.additionalEquipment?.length
                ? parseAdditionalEquipment(instrument.additionalEquipment).map(e => e.nombre).join(", ")
                : "—",
            QR_CODE_URL:           instrument?.idQrFile ? getInstrumentQrUrl(instrument.idQrFile) : "",

            // Servicio previo WIST
            is_first_visit:   previousServiceInfo?.isFirst ?? true,
            LAST_SERVICE_DATE: previousServiceInfo?.lastDate
                ? formatDateStr(previousServiceInfo.lastDate)
                : "—",

            // ── Sección 4: Motivo del servicio ──────────────────────────────
            reason_instalacion:  serviceReason === "instalacion",
            reason_mantenimiento: serviceReason === "mantenimiento-programado",
            reason_falla:        serviceReason === "falla-reportada",
            reason_revision:     serviceReason === "revision-anomalia",
            reason_otro:         serviceReason === "otro",
            SERVICE_REASON:      SERVICE_REASON_LABELS[serviceReason] ?? serviceReason ?? "—",
            REASON_DESCRIPTION:  report.reasonDescription ?? "—",
            QUALIFICATION_TYPE:  report.qualificationType ?? "—",

            // ── Sección 5: Actividades ──────────────────────────────────────
            activities: activities.map(a => ({
                order:        a.order,
                activity:     a.activity || "—",
                result:       a.result || "—",
                observations: a.observations || "—",
            })),

            // ── Sección 5B: Materiales ──────────────────────────────────────
            materials: materials.map(m => {
                const subtotal = m.quantity * m.unitCost
                const ivaAmt   = subtotal * (m.ivaPercent / 100)
                return {
                    order:       m.order,
                    name:        m.name || "—",
                    code:        m.code || "—",
                    quantity:    m.quantity,
                    condition:   m.condition || "—",
                    unit_cost:   m.unitCost,
                    iva_percent: m.ivaPercent,
                    iva_amount:  ivaAmt,
                    total:       subtotal + ivaAmt,
                }
            }),
            MATERIALS_SUBTOTAL: materials.reduce((s, m) => s + m.quantity * m.unitCost, 0),
            MATERIALS_IVA:      materials.reduce((s, m) => s + m.quantity * m.unitCost * (m.ivaPercent / 100), 0),
            MATERIALS_TOTAL:    materials.reduce((s, m) => s + m.quantity * m.unitCost * (1 + m.ivaPercent / 100), 0),

            // ── Sección 6: Resultados ───────────────────────────────────────
            status_operativo:     finalStatus === "operativo",
            status_operativo_obs: finalStatus === "operativo_observaciones",
            status_fuera_servicio: finalStatus === "fuera_servicio",
            FINAL_STATUS:         FINAL_STATUS_LABELS[finalStatus] ?? finalStatus ?? "—",

            verified_parameters: (report.verifiedParameters ?? []).map(v => ({ value: v })),
            recommendations:     (report.recommendations ?? []).map(r => ({ value: r })),

            NEXT_MAINTENANCE_DATE: formatDateStr(report.nextMaintenanceData),
            requires_remote_follow: report.requiresRemoteFollow,

            // ── Sección 7: Datos del ingeniero ──────────────────────────────
            ENGINEER_POSITION: engineerProfile?.position ?? "—",
            ENGINEER_ID:       engineerProfile?.idNumber ?? "—",
            ENGINEER_PHONE:    engineerProfile?.phone ?? "—",
            ENGINEER_EMAIL:    engineerProfile?.email ?? "—",

            // ── Sección 8: Conformidad del cliente ──────────────────────────
            CLIENT_RESPONSIBLE:          report.clientResponsible ?? "—",
            CLIENT_RESPONSIBLE_POSITION: report.clientResponsiblePosition ?? "—",
            delivered_operative:         report.deliveredOperative,
            CLIENT_OBSERVATIONS:         report.clientObservations?.join(", ") ?? "—",

            // ── Revisión ────────────────────────────────────────────────────
            IS_REVISION:     report.parentReportId != null,
            PARENT_REPORT_ID: report.parentReportId ?? "",
            REVISION_NUMBER:  report.revisionNumber ?? "",

            // ── Sección 9: Anexos ───────────────────────────────────────────
            annexes: (report.idAttachments ?? []).map(raw => {
                const { id, description } = parseAttachment(raw)
                return { url: getReportFilePreviewUrl(id), caption: description }
            }),
        },
    }

    try {
        const exec = await functions.createExecution(FUNCTION_ID, JSON.stringify(payload), false)
        const raw = exec.responseBody || exec
        let parsed: { success?: boolean; document?: string; filename?: string; error?: string }
        try {
            parsed = typeof raw === "string" ? JSON.parse(raw) : (raw as typeof parsed)
        } catch {
            parsed = {}
        }

        if (!parsed?.success) {
            return { success: false, error: parsed?.error ?? "Error al generar el documento. Por favor, inténtalo de nuevo." }
        }

        const documentBase64 = parsed.document
        if (!documentBase64) return { success: false, error: "No se recibió el documento en base64." }

        const bytes = Uint8Array.from(atob(documentBase64), c => c.charCodeAt(0))
        const blob = new Blob([bytes], {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        })
        downloadBlob(blob, parsed.filename ?? `${report.idReport ?? "informe"}.docx`)

        return { success: true }
    } catch (err: unknown) {
        return { success: false, error: err instanceof Error ? err.message : String(err) }
    }
}
